import React from 'react';
import useSurfaceNodesFromCache from 'components/canvas/hooks/use-surface-nodes-from-cache';
import useOnSurfaceChange from 'routes/surface/hooks/use-on-surface-change';
import useCanvas from 'components/canvas/hooks/use-canvas';
import { usePrevious } from 'hooks';
import { notEqual } from 'utils';
import { IRect } from 'konva/lib/types';
import { Position } from 'types';
import { isMobile } from 'react-device-detect';

interface UseScaleMiniMapInput {
  miniMapStageRef: any;
  miniMapLayerRef: any;
  width: number;
  height: number;
  nodePositionChanged: boolean;
}

interface UseScaleMiniMap {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  offsetX?: number;
  offsetY?: number;
}

export default function useMiniMapPositionAndScale({
  miniMapStageRef,
  miniMapLayerRef,
  width,
  height,
  nodePositionChanged,
}: UseScaleMiniMapInput): UseScaleMiniMap {
  const [{ scaleY, scaleX, x, y, offsetX, offsetY }, setState] = React.useState(
    {
      scaleX: 1,
      scaleY: 1,
      x: 0,
      y: 0,
      offsetX: 0,
      offsetY: 0,
    },
  );

  const {
    findMostLeftPoint,
    findMostRightPoint,
    findHighestPoint,
    findMostLowestPoint,
  } = useCanvas();

  const shouldUpdateScale = useShouldUpdateScale();

  React.useEffect(() => {
    // Update the scale for mini map
    if (miniMapStageRef.current && miniMapLayerRef.current) {
      const scale = Math.min(calculateScaleX(), calculateScaleY());

      const position = getMiniMapPosition(calculateScaleX(), calculateScaleY());

      if (isPositionValid(position)) {
        setState({
          ...position,
          scaleX: scale,
          scaleY: scale,
          offsetX: 0,
          offsetY: 0,
        });
      }
    }
  }, [shouldUpdateScale, nodePositionChanged]);

  React.useEffect(() => {
    setMiniMapPosition();
  }, [nodePositionChanged]);

  useOnSurfaceChange(() => setMiniMapPosition());

  function setMiniMapPosition() {
    if (miniMapStageRef.current && miniMapLayerRef.current) {
      if (scaleX && scaleY) {
        const position = getMiniMapPosition(scaleX, scaleY);

        if (isPositionValid(position)) {
          setState((previousState) => ({
            ...previousState,
            ...position,
          }));
        }
      }
    }
  }

  function getMiniMapPosition(scaleX, scaleY) {
    const box = {
      x: findMostLeftPoint(),
      y: findHighestPoint(),
    };

    return {
      x: -box.x * scaleX,
      y: -box.y * scaleY,
    };
  }

  function calculateScaleX(): number {
    const box = getMainCanvasRect();

    return width / box?.width;
  }

  function calculateScaleY(): number {
    const box = getMainCanvasRect();

    return height / box?.height;
  }

  function getMainCanvasRect(): IRect {
    const width = Math.abs(findMostRightPoint() - findMostLeftPoint());

    const height = Math.abs(findHighestPoint() - findMostLowestPoint());

    return { width, height } as any;
  }

  return { x, y, scaleY, scaleX, offsetX, offsetY };
}

function isPositionValid(position: Position): boolean {
  function isPositionValueValid(number: number) {
    return isFinite(number) && isNaN(number) === false;
  }
  return isPositionValueValid(position.x) && isPositionValueValid(position.y);
}

function useShouldUpdateScale(): boolean {
  const nodes = useSurfaceNodesFromCache();

  const nodesPositionMap = nodes.reduce(
    (hash, node) => ({
      ...hash,
      [node.id]: `${node?.position?.x}${node?.position?.y}`,
    }),
    {},
  );

  const previousNodesPositionMap = usePrevious(nodesPositionMap);

  return notEqual(nodesPositionMap, previousNodesPositionMap);
}
